"use client";

import { useEffect } from "react";
import type { LifecycleStatus } from "@/domain/catalog";
import { AddToCartButtonView } from "./AddToCartButtonView";
import { deriveAddToCartState } from "./derive-add-to-cart-state";
import { useAddToCart } from "./use-add-to-cart";

export interface AddToCartButtonProps {
	sku: string;
	/** Preferred over `sku` when set. */
	variantSku?: string;
	quantity: number;

	lifecycleStatus?: LifecycleStatus;
	outOfStock?: boolean;
	/** Triggers the Host-a-Party CTA. */
	hostOnly?: boolean;
	consultantOnly?: boolean;
	isConsultant?: boolean;

	/** Subscribe at the page level for a global error banner / toast. */
	onError?: (message: string) => void;
	/** Subscribe at the page level to open a cart slider, fire analytics, etc. */
	onSuccess?: () => void;

	fullWidth?: boolean;
	className?: string;
}

/**
 * Wired Add-to-Cart button — composes the `useAddToCart` hook with a
 * derived discriminated state, then renders `AddToCartButtonView`.
 */
export function AddToCartButton({
	sku,
	variantSku,
	quantity,
	lifecycleStatus = "active",
	outOfStock,
	hostOnly,
	consultantOnly,
	isConsultant = false,
	onError,
	onSuccess,
	fullWidth,
	className
}: AddToCartButtonProps) {
	const { handleClick, loading, error, submitted } = useAddToCart({
		sku,
		variantSku,
		quantity
	});

	useEffect(() => {
		if (error && onError) onError(error);
	}, [error, onError]);

	useEffect(() => {
		if (submitted && onSuccess) onSuccess();
	}, [submitted, onSuccess]);

	const state = deriveAddToCartState(
		{
			status: lifecycleStatus,
			outOfStock: outOfStock ?? false,
			hostOnly: hostOnly ?? false,
			consultantOnly: consultantOnly ?? false
		},
		{ isConsultant, loading, error, submitted }
	);

	return (
		<AddToCartButtonView
			state={state}
			onClick={handleClick}
			fullWidth={fullWidth}
			className={className}
		/>
	);
}
